import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { Badge, Button, Card, PageHeader, Section } from '@/components/ui';
import { VerticalIntegrationChart } from '@/components/charts';
import { SITE } from '@/lib/site';

const layers = [
  { name: 'Access', detail: 'Internal chat, web and messaging — plus an MCP endpoint, so AI agents can query and transact too' },
  { name: 'Application', detail: 'Conversational services, confirmation gates before any write, transactional tools' },
  { name: 'Retrieval', detail: 'Grounded index over your content, filtered by each user’s real permissions' },
  { name: 'Connectors', detail: 'Identity, content repositories, ERP, CRM, service management, databases' },
  { name: 'Control', detail: 'Admission, budgets, priority, cancellation, routing and placement policy' },
  { name: 'Inference', detail: 'The Fnk engine, serving open-weight models inside your environment' },
  { name: 'Evidence', detail: 'One trace from business action to cost, latency, model version and audit record' },
  { name: 'Operations', detail: 'Console, dashboards, backup, upgrade and rollback' },
];

const contract = [
  ['Bidirectional capacity contract', 'The engine advertises real admission capacity upward as leases; the framework stops admitting work a saturated runtime cannot finish', 'HTTP returns a result or an error, never a capacity signal'],
  ['End-to-end cancellation with reclaim accounting', 'When a user abandons, generation stops at the GPU — and the reclaimed compute is counted', 'Cancellation stops at the HTTP boundary; the engine keeps generating dead work'],
  ['Business-transaction budgets', 'Token, tool, call and time budgets for the whole transaction, not per request — agent fan-out stays bounded', 'Frameworks count steps, engines count requests, nobody counts the transaction'],
  ['Admission-aware scheduling', 'By business priority and remaining budget, not arrival order', 'Engine schedulers cannot see business priority'],
  ['Unified evidence plane', 'One trace from business action to tokens, queue time, saturation and cost', 'Two telemetry systems that cannot be joined'],
  ['Replay-based sizing', 'The next accelerator purchase is sized against recorded load, not a peak guess', 'Capacity is a guess made at procurement time'],
];

const harness = [
  ['Vertical workflow packs', 'Intent-to-transaction flows per service type'],
  ['Tool and action schemas', 'Tool definitions and the action cards that carry them'],
  ['Prompt and policy packs', 'Escalation, refusal and handover thresholds; Spanish and co-official languages'],
  ['Guardrails and confirmation gates', 'Nothing that costs money executes without explicit confirmation'],
  ['Grounding configuration', 'Retrieval bound to each user’s real entitlements in the source system'],
  ['Evaluation sets', 'The customer’s own questions, rerun on every model or connector upgrade, with rollback'],
];

const forms = [
  { name: 'Stack licence', price: 'Per GPU, per year', who: 'Inference providers, data centres, enterprise GPU estates, OEMs', points: ['Framework, control layer and inference engine', 'On the GPUs you already run', 'One list price per hardware class', 'Harness priced in the negotiation'] },
  { name: 'Partial box', price: 'On your own hardware', who: 'Enterprises with a data centre', points: ['Stack, Harness, connectors and console', 'Installed on hardware you already own', 'Against a published specification', 'Fits your procurement standards'] },
  { name: 'Full box', price: 'On licensed hardware', who: 'Regulated organisations without a platform team', points: ['The same build, preinstalled', 'Delivered ready to rack', 'Air-gapped where required', 'One vendor accountable for the whole system'] },
];

const connections = [
  { title: 'Content', detail: 'Document repositories, wikis, portals, policy libraries' },
  { title: 'Identity', detail: 'LDAP, Active Directory and SSO — including group and role mapping' },
  { title: 'Systems of record', detail: 'ERP, CRM, service management, HR, calendars' },
  { title: 'Your own services', detail: 'Databases, warehouses and internal APIs' },
];

const platforms = [
  ['NVIDIA CUDA', 'Paired benchmarks on GeForce RTX 4090 and DGX Spark (Grace Blackwell), TensorRT-LLM integration', 'The data-centre path and the default box'],
  ['Apple Silicon / MLX', '1.8× end-to-end throughput; 2.8× faster time-to-first-token against LM Studio', 'Corroboration on unified memory; the edge and dev path'],
  ['AMD CPU/GPU', 'Backend arm of the benchmark already runs on AMD Ryzen; engine port on the roadmap', 'Second-source hardware, and price leverage in box configurations'],
];

export function ProductPage() {
  return (
    <div>
      <PageHeader
        kicker="Product"
        title={<span>The stack, the Harness, and three ways to buy them.</span>}
        lead="Fnk turns open-weight models into a governed, private, efficient AI service. It starts where the assembled stack breaks: the boundary between the framework and the engine."
        actions={
          <>
            <a href={SITE.assessmentMailto} className="w-full sm:w-auto">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>Request an assessment</Button>
            </a>
            <Link to="/pricing" className="w-full sm:w-auto">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>Pricing</Button>
            </Link>
          </>
        }
      />

      <Section n="01" kicker="The Fnk AI stack" title="What the stack does that the assembled stack cannot." lead="Framework, control layer and inference engine, built as one non-blocking system. The advantage lives in the control plane, not in accelerator kernels — which is why it ports.">
        <Card variant="raised" className="overflow-x-auto p-4 sm:p-5">
          <table className="w-full min-w-[640px] border-collapse text-left">
            <thead>
              <tr className="border-b border-ink">
                <th className="label py-2.5 pr-4 normal-case">Property</th>
                <th className="label py-2.5 pr-4 normal-case">The Fnk stack</th>
                <th className="label py-2.5 normal-case">Why the assembled stack cannot</th>
              </tr>
            </thead>
            <tbody>
              {contract.map((row) => (
                <tr key={row[0]} className="border-b border-line last:border-b-0">
                  <td className="py-2.5 pr-4 align-top text-[13px] font-medium text-ink">{row[0]}</td>
                  <td className="py-2.5 pr-4 align-top text-[13px] leading-relaxed text-ink-2">{row[1]}</td>
                  <td className="py-2.5 align-top text-[13px] leading-relaxed text-ink-3">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <div className="mt-8">
          <VerticalIntegrationChart />
        </div>
      </Section>

      <Section n="02" kicker="The Fnk Harness" title="The configured layer that makes a stack a system." lead="A model is not a product. The Harness turns the stack into a business result — and it names a policy, never a provider, so the same pack runs on Fnk’s engine or against an approved endpoint." tone="white">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {harness.map(([title, detail]) => (
            <Card key={title} variant="plain" className="p-5">
              <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">{detail}</p>
            </Card>
          ))}
        </div>
        <p className="mt-6 text-sm leading-relaxed text-ink-2">
          It ships inside every box. For a stack-licence buyer it is priced in the negotiation.
        </p>
      </Section>

      <Section n="03" kicker="Three ways to buy" title="A licence on your GPUs, or a box.">
        <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
          {forms.map((f, i) => (
            <Card key={f.name} variant={i === 0 ? 'accent' : 'raised'} className="flex h-full flex-col p-6">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-lg font-semibold tracking-tight">{f.name}</h3>
                {i === 0 && <Badge variant="accent">Upside line</Badge>}
              </div>
              <p className="mt-1 font-mono text-xs tabular text-accent-ink">{f.price}</p>
              <p className="mt-3 text-[13px] text-ink-3">{f.who}</p>
              <ul className="mt-4 flex-1 space-y-2">
                {f.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-[13px] text-ink-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-ok" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-sm leading-relaxed text-ink-2">
          Both boxes are the same build; they differ only in who owns the hardware. Every engagement starts with a <strong className="font-semibold text-ink">paid assessment</strong> —
          your workload replayed on the stack — before anything larger is quoted.
        </p>
      </Section>

      <Section n="04" kicker="What is in the box" title="Eight layers, one accountable party." tone="white">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {layers.map((l) => (
            <Card key={l.name} variant="plain" className="p-4">
              <h3 className="text-sm font-semibold text-accent-ink">{l.name}</h3>
              <p className="mt-1 text-xs leading-relaxed text-ink-2">{l.detail}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section n="05" kicker="What you connect" title="Retrieval filtered by each user’s real entitlements." lead="Nobody gets an answer from a document they could not open themselves — the most common way AI pilots fail a security review.">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {connections.map((c) => (
            <Card key={c.title} variant="raised" className="p-5">
              <h3 className="text-sm font-semibold tracking-tight">{c.title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-ink-2">{c.detail}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section n="06" kicker="Hardware and model independence" title="Bound to no supplier." lead="The advantage is in scheduling, admission and cancellation, not in CUDA kernels. Models are open-weight and swappable: the rule is the smallest model that passes your own quality, safety and language tests." tone="white">
        <Card variant="raised" className="overflow-x-auto p-4 sm:p-5">
          <table className="w-full min-w-[560px] border-collapse text-left">
            <thead>
              <tr className="border-b border-ink">
                <th className="label py-2.5 pr-4 normal-case">Platform</th>
                <th className="label py-2.5 pr-4 normal-case">Evidence today</th>
                <th className="label py-2.5 normal-case">Role</th>
              </tr>
            </thead>
            <tbody>
              {platforms.map((row) => (
                <tr key={row[0]} className="border-b border-line last:border-b-0">
                  <td className="py-2.5 pr-4 align-top text-[13px] font-medium text-ink">{row[0]}</td>
                  <td className="py-2.5 pr-4 align-top text-[13px] leading-relaxed text-ink-2">{row[1]}</td>
                  <td className="py-2.5 align-top text-[13px] leading-relaxed text-ink-3">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card variant="plain" className="mt-6 p-5">
          <p className="label mb-2">What the customer never has to do</p>
          <p className="text-sm leading-relaxed text-ink-2">
            Choose an inference engine, tune batching, build a retrieval pipeline, write identity integration, build connectors, assemble
            observability, or hire an ML platform engineer.
          </p>
        </Card>
      </Section>

      <section className="border-t border-line">
        <div className="mx-auto max-w-site px-4 py-14 sm:px-6 md:py-20">
          <Card variant="accent" className="p-6 sm:p-8 md:p-10">
            <h2 className="text-section-mobile font-semibold tracking-heading md:text-section-desktop">Start with the assessment.</h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink-2">Three weeks, a fixed scope, and a number measured on your own workload.</p>
            <div className="mt-6">
              <a href={SITE.assessmentMailto} className="inline-block w-full sm:w-auto">
                <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>Request an assessment</Button>
              </a>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
